"use client"

import { useEffect } from "react"
import { reportClientError } from "./client-error-report"
import {
  isStaleServerActionError,
  reloadForStaleDeploy,
} from "./stale-deploy"

export default function ClientErrorListener() {
  useEffect(() => {
    const isNextRedirect = (err: unknown): boolean => {
      if (!err || typeof err !== "object") return false
      const e = err as Record<string, unknown>
      return (
        e["message"] === "NEXT_REDIRECT" ||
        (typeof e["digest"] === "string" && e["digest"].startsWith("NEXT_REDIRECT"))
      )
    }

    // Tabs left open across a deploy reference server-action IDs that no longer
    // exist in the new build; every button wired to one is dead until the tab
    // reloads. Detection + one-shot reload live in ./stale-deploy so the local
    // try/catch blocks in cart/checkout components share the exact same logic
    // (they swallow the error, so this global listener never sees those paths).
    // A stale-deploy skew self-heals with a one-shot reload onto the current
    // build; it is expected after every redeploy and not a real client error, so
    // it should not be logged as one. Only when the reload is SUPPRESSED
    // (reloadForStaleDeploy() returns false → already reloaded once this session,
    // i.e. the skew is persistent, not the transient kind a reload fixes) do we
    // fall through and report it.
    const onError = (event: ErrorEvent) => {
      const error = event.error || event.message
      // NEXT_REDIRECT is Next.js's redirect control-flow, not a real error.
      if (isNextRedirect(error)) return
      if (isStaleServerActionError(error) && reloadForStaleDeploy()) return
      reportClientError({ source: "window-error", error })
    }

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      // NEXT_REDIRECT is Next.js's redirect control-flow, not a real error.
      if (isNextRedirect(reason)) return
      if (isStaleServerActionError(reason) && reloadForStaleDeploy()) return
      reportClientError({ source: "unhandled-rejection", error: reason })
    }

    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onUnhandledRejection)

    return () => {
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onUnhandledRejection)
    }
  }, [])

  return null
}
